import { useMemo, useState } from 'react';

export interface SelectForm {
  value: string;
  label: string;
}

export interface MultipleReactSelectInput {
  value: SelectForm[] | undefined;
  options: SelectForm[];
  onChange: (values: ReadonlyArray<SelectForm>) => void;
  onReset: () => void;
}

const isReadonlyArrayOfStrings = <T extends string>(
  value: unknown
): value is readonly T[] => {
  return (
    Array.isArray(value) && value.every((item) => typeof item === 'string')
  );
};

// デフォルトの値はクエリパラメータから取得するためstring[]になっている
// そのため、SelectForm[]から合致するアイテムを探し、配列に格納する必要がある
const getDefaultFormValue = <T extends string>(
  list: readonly T[] | Record<'value' | 'label', T>[],
  defaultValue?: T[]
): SelectForm[] | undefined => {
  if (defaultValue === undefined) return;

  return defaultValue.reduce((result: SelectForm[], current) => {
    if (isReadonlyArrayOfStrings<T>(list)) {
      const target = list.find((item) => item === current);
      if (target === undefined) return result;

      return [...result, { value: target, label: target }];
    }

    const target = list.find((item) => item.value === current);
    if (target === undefined) return result;

    return [...result, target];
  }, []);
};

/***
 * @targetSelect 変更対象のセレクトボックス
 * @search 検索の関数
 * @selectList セレクトボックスのoptionsが作られる
 * @defaultValue 初期値
 ***/
export const useMultipleReactSelectInput = <T extends string>(
  list: readonly T[] | Record<'value' | 'label', T>[],
  defaultValue?: T[]
): MultipleReactSelectInput => {
  const [value, setValue] = useState<SelectForm[] | undefined>(
    getDefaultFormValue(list, defaultValue)
  );

  const options: SelectForm[] = useMemo(() => {
    if (isReadonlyArrayOfStrings<T>(list))
      return list.map((item) => {
        return { value: item, label: item };
      });

    return list.map((item: Record<'value' | 'label', string>) => {
      return { value: item.value, label: item.label };
    });
  }, [list]);

  const onChange = (values: ReadonlyArray<SelectForm>) => {
    setValue([...values]);
  };

  const onReset = () => setValue([]);

  return { value, options, onChange, onReset };
};
